"use client";

import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "convex/react";
import { useEffect, useRef, useState } from "react";
import { api } from "../../../../convex/_generated/api";
import { useEntitlementVerify } from "@/hooks/useEntitlementVerify";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { CheckIcon } from "@/components/icons";
import { usePageTitle } from "@/lib/usePageTitle";
import { WelcomeOnboardingModal } from "@/components/checkout/WelcomeOnboardingModal";
import {
  getServerLimitForSubscription,
  type SubscriptionStatus,
  type SubscriptionTier,
} from "@/lib/billing/plans";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function planLabel(plan: string | null): string | null {
  if (!plan) return null;
  const MAP: Record<string, string> = {
    starter: "Founder Starter",
    plus: "Founder Plus",
    pro: "Founder Pro",
    basic: "Basic",
    enterprise: "Enterprise",
  };
  return MAP[plan] ?? null;
}

function tierLabel(tier: SubscriptionTier): string {
  if (tier === "free") return "Free";
  return `Founder ${tier.charAt(0).toUpperCase()}${tier.slice(1)}`;
}

function statusLabel(status: SubscriptionStatus): string {
  if (status === "none") return "No active subscription";
  return status.replace(/_/g, " ");
}

function provisionStatusBadge(provisionStatus: string) {
  switch (provisionStatus) {
    case "active":
      return <Badge variant="success">Running</Badge>;
    case "pending":
      return <Badge variant="secondary">Provisioning…</Badge>;
    case "error":
      return <Badge variant="destructive">Error</Badge>;
    default:
      return <Badge variant="outline">{provisionStatus}</Badge>;
  }
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function CheckoutSuccessPage() {
  usePageTitle("Checkout Successful");

  // Fire a Polar.sh entitlement check on mount so the subscription gate has
  // an authoritative result ready without waiting for the webhook.
  useEntitlementVerify();

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // URL params set by the Convex /api/polar/checkout/success verification gate
  const checkoutId = searchParams.get("checkout_id");
  const planParam = searchParams.get("plan");
  const isVerified = searchParams.get("verified") === "true";

  const requestedPlanLabel = planLabel(planParam);

  // Convex reactive queries
  const summary = useQuery(api.billing.getMySummary);
  const postPurchaseOverview = useQuery(api.dashboard.getPostPurchaseOverview);

  // Pending checkout state — reflects the checkout session status before the
  // Polar webhook fires. This is the "pending subscription state" that bridges
  // the gap between checkout completion and webhook confirmation.
  const pendingCheckout = useQuery(api.billing.getMyPendingCheckout, {
    checkoutId: checkoutId ?? undefined,
  });

  // Mutation: mark payment flow validated (once)
  const markValidated = useMutation(api.billing.markPaymentFlowValidated);
  const validatedRef = useRef(false);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const modalShownRef = useRef(false);

  // Auto-redirect countdown
  const [countdown, setCountdown] = useState<number | null>(null);

  const tier = (summary?.tier ?? "free") as SubscriptionTier;
  const status = (summary?.status ?? "none") as SubscriptionStatus;
  const isActivated =
    tier !== "free" && (status === "active" || status === "trialing");

  const serverLimit = getServerLimitForSubscription(tier, status);

  const renewalDate = summary?.currentPeriodEnd
    ? new Date(summary.currentPeriodEnd).toLocaleDateString(undefined, {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : null;

  const trialEndDate =
    status === "trialing" && summary?.trialEndsAt
      ? new Date(summary.trialEndsAt).toLocaleDateString(undefined, {
          month: "long",
          day: "numeric",
          year: "numeric",
        })
      : null;

  // Mark payment_flow_validated once when subscription is first confirmed active
  useEffect(() => {
    if (!isActivated || validatedRef.current) return;
    validatedRef.current = true;
    markValidated({}).catch((err) => {
      console.error("[checkout/success] markPaymentFlowValidated failed:", err);
    });
  }, [isActivated, markValidated]);

  // Show onboarding modal once when subscription becomes active
  useEffect(() => {
    if (!isActivated || modalShownRef.current) return;
    modalShownRef.current = true;
    // Small delay so the page content renders first
    const t = setTimeout(() => setShowModal(true), 600);
    return () => clearTimeout(t);
  }, [isActivated]);

  // Auto-redirect to dashboard once subscription confirmed + modal dismissed
  useEffect(() => {
    if (!isActivated || showModal) return;
    if (countdown !== null) return; // Already counting
    setCountdown(5);
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev === null || prev <= 1) {
          clearInterval(interval);
          navigate("/dashboard?checkout=success", { replace: true });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActivated, showModal]);

  const handleModalDismiss = () => {
    setShowModal(false);
  };

  const server = postPurchaseOverview?.server ?? null;

  return (
    <>
      <WelcomeOnboardingModal
        open={showModal}
        onDismiss={handleModalDismiss}
        planName={requestedPlanLabel ?? tierLabel(tier)}
        serverLimit={serverLimit}
      />

      <div className="mx-auto max-w-3xl space-y-6">
        {/* Checkout confirmation banner */}
        <Card className="border-green-300 bg-green-50/60 dark:border-green-700 dark:bg-green-950/20">
          <CardHeader>
            <div className="mb-2 inline-flex h-10 w-10 items-center justify-center rounded-full bg-green-600/15 text-green-700 dark:text-green-300">
              <CheckIcon className="h-5 w-5" />
            </div>
            <CardTitle>Payment Successful</CardTitle>
            <CardDescription>
              {isVerified
                ? "Polar verified your checkout. Your subscription is syncing now."
                : "Checkout complete. Your subscription is syncing from Polar.sh."}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-muted">
            {requestedPlanLabel && (
              <p>
                Plan:{" "}
                <span className="font-medium text-foreground">
                  {requestedPlanLabel}
                </span>
              </p>
            )}
            {checkoutId && (
              <p>
                Reference:{" "}
                <span className="font-mono text-xs text-foreground">
                  {checkoutId}
                </span>
              </p>
            )}
            {/* Pending checkout state — reflects Polar.sh session status before webhook */}
            {pendingCheckout && (
              <p>
                Checkout status:{" "}
                <span className="font-medium capitalize text-foreground">
                  {pendingCheckout.status}
                </span>
                {pendingCheckout.withTrial && (
                  <span className="ml-2 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-300">
                    Trial
                  </span>
                )}
              </p>
            )}
            {isVerified && (
              <p className="text-green-700 dark:text-green-400 font-medium">
                ✓ Checkout verified by Polar.sh
              </p>
            )}
          </CardContent>
        </Card>

        {/* Subscription status card */}
        {summary === undefined ? (
          <Card>
            <CardHeader>
              <CardTitle>Confirming Subscription</CardTitle>
              <CardDescription>
                Waiting for your subscription status from Polar.sh…
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <Skeleton className="h-4 w-44" />
              <Skeleton className="h-4 w-64" />
              <Skeleton className="h-4 w-52" />
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader className="space-y-2">
              <div className="flex items-center gap-2">
                <CardTitle>Subscription Status</CardTitle>
                <Badge variant={isActivated ? "success" : "secondary"}>
                  {isActivated ? "Active" : "Pending"}
                </Badge>
              </div>
              <CardDescription>
                {isActivated
                  ? "Your subscription is confirmed and active."
                  : "Payment received — waiting for Polar.sh webhook to finalize the subscription."}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2 text-sm text-muted">
              <p>
                Plan:{" "}
                <span className="font-medium capitalize text-foreground">
                  {tierLabel(tier)}
                </span>
              </p>
              <p>
                Status:{" "}
                <span className="font-medium capitalize text-foreground">
                  {statusLabel(status)}
                </span>
              </p>
              {serverLimit > 0 && (
                <p>
                  Organization limit:{" "}
                  <span className="font-medium text-foreground">
                    {serverLimit}
                  </span>
                </p>
              )}
              {trialEndDate && (
                <p>
                  Trial ends:{" "}
                  <span className="font-medium text-foreground">
                    {trialEndDate}
                  </span>
                </p>
              )}
              {!trialEndDate && renewalDate && (
                <p>
                  Next billing date:{" "}
                  <span className="font-medium text-foreground">
                    {renewalDate}
                  </span>
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Server provisioning status — only shown once subscription is active */}
        {isActivated && server && (
          <Card>
            <CardHeader className="space-y-2">
              <div className="flex items-center gap-2">
                <CardTitle>Game Server</CardTitle>
                {provisionStatusBadge(server.provisionStatus)}
              </div>
              <CardDescription>
                {server.provisionStatus === "pending"
                  ? "Your dedicated Hytale game server is being provisioned. This usually takes 1–3 minutes."
                  : server.provisionStatus === "active"
                    ? "Your game server is ready."
                    : server.provisionStatus === "error"
                      ? "There was an issue provisioning your server. Our team has been notified."
                      : "Checking server status…"}
              </CardDescription>
            </CardHeader>
            {(server.serverIp || server.vultrRegion) && (
              <CardContent className="space-y-2 text-sm text-muted">
                {server.serverIp && (
                  <p>
                    Server IP:{" "}
                    <span className="font-mono font-medium text-foreground">
                      {server.serverIp}:{server.serverPort}
                    </span>
                  </p>
                )}
                {server.vultrRegion && (
                  <p>
                    Region:{" "}
                    <span className="font-medium text-foreground">
                      {server.vultrRegion}
                    </span>
                  </p>
                )}
                {server.provisionError && (
                  <p className="text-destructive">{server.provisionError}</p>
                )}
              </CardContent>
            )}
          </Card>
        )}

        {/* Onboarding guide — shown once subscription is active, before modal */}
        {isActivated && !showModal && (
          <Card className="border-blue-200 bg-blue-50/50 dark:border-blue-800 dark:bg-blue-950/20">
            <CardHeader>
              <CardTitle className="text-base">
                Getting Started with BlockyNetworks
              </CardTitle>
              <CardDescription>
                Follow these steps to get your Hytale server live.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <ol className="list-inside list-decimal space-y-2 text-muted">
                <li>
                  <span className="font-medium text-foreground">
                    Create an organization
                  </span>{" "}
                  — your server community hub with members, chat, and analytics.
                </li>
                <li>
                  <span className="font-medium text-foreground">
                    Install the BlockyNetwork plugin
                  </span>{" "}
                  — download the plugin JAR and add it to your Hytale server&rsquo;s{" "}
                  <code className="rounded bg-muted/50 px-1 py-0.5 text-xs">
                    plugins/
                  </code>{" "}
                  folder.
                </li>
                <li>
                  <span className="font-medium text-foreground">
                    Link your server
                  </span>{" "}
                  — use your organization&rsquo;s API key to connect your Hytale
                  server. Heartbeats start automatically.
                </li>
                <li>
                  <span className="font-medium text-foreground">
                    Go live in Server Finder
                  </span>{" "}
                  — set your organization to Public and players can discover your
                  server.
                </li>
              </ol>
              {serverLimit > 0 && (
                <p className="text-xs text-muted">
                  Your plan supports up to{" "}
                  <span className="font-medium text-foreground">
                    {serverLimit}
                  </span>{" "}
                  organization{serverLimit !== 1 ? "s" : ""}.
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Auto-redirect countdown */}
        {isActivated && !showModal && countdown !== null && countdown > 0 && (
          <p className="text-sm text-muted">
            Redirecting to dashboard in {countdown}s&hellip;{" "}
            <button
              onClick={() => {
                setCountdown(null);
                navigate("/dashboard?checkout=success", { replace: true });
              }}
              className="text-primary hover:underline"
            >
              Go now
            </button>
          </p>
        )}

        {/* Action buttons */}
        <div className="flex flex-wrap gap-3">
          <Link to="/dashboard">
            <Button>Go to Dashboard</Button>
          </Link>
          <Link to="/dashboard/settings/billing">
            <Button variant="outline">Manage Billing</Button>
          </Link>
          {isActivated && (
            <button
              onClick={() => setShowModal(true)}
              className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-foreground/5 transition-colors"
            >
              View Setup Guide
            </button>
          )}
        </div>
      </div>
    </>
  );
}
